/**
 * src/components/shared/RejectedLeadsModal.jsx -- TICKET-207
 *
 * Modal listing emails the AI filtered out as non-leads, pending CEO review.
 * Each row: from_name <from_email>, subject, snippet, AI reason, confidence, date.
 * Actions: "It's a lead" (promote) or "Dismiss" (confirm AI was right).
 * Mirrors ComposeEmailModal overlay/sheet structure.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader } from 'lucide-react';
import { ModalSheet } from '../SharedUI';
import { useT } from '../../lib/i18n/I18nContext';
import { useToast } from './ToastProvider';
import { listRejectedLeads, promoteRejection, dismissRejection } from '../../lib/api/leads.js';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

function formatConfidence(val) {
  if (val == null) return '';
  const pct = Math.round((typeof val === 'number' ? val : parseFloat(val)) * 100);
  return `AI ${pct}%`;
}

export default function RejectedLeadsModal({ open, onClose }) {
  const { t } = useT();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejections, setRejections] = useState([]);
  const [acting, setActing] = useState({}); // { [id]: 'promoting' | 'dismissing' }

  // Stable refs so callbacks don't recreate on every render (avoids useEffect loop).
  const tRef = useRef(t);
  const showSuccessRef = useRef(showSuccess);
  const showErrorRef = useRef(showError);
  tRef.current = t;
  showSuccessRef.current = showSuccess;
  showErrorRef.current = showError;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listRejectedLeads();
      if (res?.ok) {
        setRejections(res.rejections ?? []);
      } else {
        setError(tRef.current('rejectedLeads.error'));
      }
    } catch (err) {
      console.warn('[RejectedLeadsModal] load failed:', err?.message);
      setError(tRef.current('rejectedLeads.error'));
    } finally {
      setLoading(false);
    }
  }, []); // stable -- no deps that should trigger reload

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handlePromote = useCallback(async (id) => {
    setActing(prev => ({ ...prev, [id]: 'promoting' }));
    setRejections(prev => {
      const snapshot = [...prev];
      // Store snapshot for potential revert
      handlePromote._snapshots = handlePromote._snapshots || {};
      handlePromote._snapshots[id] = snapshot;
      return prev.filter(r => r.id !== id);
    });
    try {
      const res = await promoteRejection(id);
      if (res?.ok) {
        showSuccessRef.current(tRef.current('rejectedLeads.promoted'));
      } else {
        setRejections(handlePromote._snapshots?.[id] ?? []);
        showErrorRef.current(tRef.current('rejectedLeads.error'));
      }
    } catch (err) {
      console.warn('[RejectedLeadsModal] promote failed:', err?.message);
      setRejections(handlePromote._snapshots?.[id] ?? []);
      showErrorRef.current(tRef.current('rejectedLeads.error'));
    } finally {
      setActing(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }, []); // stable

  const handleDismiss = useCallback(async (id) => {
    setActing(prev => ({ ...prev, [id]: 'dismissing' }));
    setRejections(prev => {
      const snapshot = [...prev];
      handleDismiss._snapshots = handleDismiss._snapshots || {};
      handleDismiss._snapshots[id] = snapshot;
      return prev.filter(r => r.id !== id);
    });
    try {
      const res = await dismissRejection(id);
      if (res?.ok) {
        showSuccessRef.current(tRef.current('rejectedLeads.dismissed'));
      } else {
        setRejections(handleDismiss._snapshots?.[id] ?? []);
        showErrorRef.current(tRef.current('rejectedLeads.error'));
      }
    } catch (err) {
      console.warn('[RejectedLeadsModal] dismiss failed:', err?.message);
      setRejections(handleDismiss._snapshots?.[id] ?? []);
      showErrorRef.current(tRef.current('rejectedLeads.error'));
    } finally {
      setActing(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }, []); // stable

  const cardStyle = {
    background: 'var(--card)', borderRadius: 12,
    boxShadow: 'inset 0 0 0 1px var(--bdr)',
    padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6,
  };
  const btnBase = {
    minHeight: 44, padding: '8px 14px', borderRadius: 10, border: 'none',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
    transition: 'all 150ms var(--ease)',
  };

  return (
    <ModalSheet
      open={open}
      onClose={onClose}
      title={`${t('rejectedLeads.title')}${rejections.length > 0 ? ` (${rejections.length})` : ''}`}
      id="rejected-leads"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 2px' }}>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0', color: 'var(--t2)' }}>
            <Loader size={22} strokeWidth={1.75} style={{ animation: 'spin 800ms linear infinite' }} />
          </div>
        )}

        {!loading && error && (
          <div style={{ color: 'var(--bad)', fontSize: 14, padding: '16px 0', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {!loading && !error && rejections.length === 0 && (
          <div
            data-testid="rejected-leads-empty"
            style={{ color: 'var(--t2)', fontSize: 14, padding: '32px 0', textAlign: 'center' }}
          >
            {t('rejectedLeads.empty')}
          </div>
        )}

        {!loading && !error && rejections.map((r) => {
          const isActing = !!acting[r.id];
          return (
            <div key={r.id} style={cardStyle}>
              {/* Header row: name + date */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.from_name || r.from_email}
                  {r.from_name && (
                    <span style={{ fontWeight: 400, color: 'var(--t2)', marginLeft: 4 }}>
                      &lt;{r.from_email}&gt;
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--tm)', flexShrink: 0 }}>
                  {formatDate(r.received_at)}
                </div>
              </div>

              {/* Subject */}
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>
                {r.subject}
              </div>

              {/* Snippet */}
              {r.snippet && (
                <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {r.snippet}
                </div>
              )}

              {/* AI reason + confidence */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {r.ai_reason && (
                  <span style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--tm)' }}>
                    {t('rejectedLeads.aiReason')}: {r.ai_reason}
                  </span>
                )}
                {r.confidence != null && (
                  <span style={{ fontSize: 11, color: 'var(--tm)', background: 'var(--glass)', borderRadius: 6, padding: '2px 6px' }}>
                    {formatConfidence(r.confidence)}
                  </span>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  data-testid="rejected-lead-promote"
                  disabled={isActing}
                  onClick={() => handlePromote(r.id)}
                  style={{
                    ...btnBase,
                    flex: 1,
                    background: isActing ? 'var(--accent-bg)' : 'var(--accent)',
                    color: isActing ? 'var(--accent)' : 'var(--card)',
                    cursor: isActing ? 'default' : 'pointer',
                    opacity: isActing ? 0.6 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  {acting[r.id] === 'promoting'
                    ? <><Loader size={14} strokeWidth={1.75} style={{ animation: 'spin 800ms linear infinite' }} />{t('rejectedLeads.promoting')}</>
                    : t('rejectedLeads.itsALead')}
                </button>
                <button
                  type="button"
                  data-testid="rejected-lead-dismiss"
                  disabled={isActing}
                  onClick={() => handleDismiss(r.id)}
                  style={{
                    ...btnBase,
                    background: 'transparent',
                    color: 'var(--t2)',
                    cursor: isActing ? 'default' : 'pointer',
                    opacity: isActing ? 0.5 : 1,
                    boxShadow: 'inset 0 0 0 1px var(--bdr)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  {acting[r.id] === 'dismissing'
                    ? <><Loader size={14} strokeWidth={1.75} style={{ animation: 'spin 800ms linear infinite' }} />{t('rejectedLeads.dismissing')}</>
                    : t('rejectedLeads.dismiss')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ModalSheet>
  );
}
